/**
 * Coverage-create dialog seeded by an `insurance_card` extraction.
 *
 * The provider can edit any field before submitting. On save, we POST
 * the existing coverage create endpoint with the standard fields plus
 * the GDN-1032 follow-up additions:
 *   - source_document_reference_id
 *   - source_extraction_kind: 'insurance_card'
 *
 * Sending those two fields causes the BE to atomically stamp
 * `acted_on_at` / `acted_on_by` on the DocRef extraction in the same
 * transaction — so a refetch of the DocRef detail will hide the banner
 * without us calling any "mark acted on" endpoint.
 */

import { useEffect, useState } from 'react';
import { useCreatePatientCoverageMutation } from '@features/patient-coverages';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import type { InsuranceCardExtraction } from './extractionTypes';

const RELATIONSHIP_OPTIONS = ['self', 'spouse', 'child', 'other'];

interface SaveExtractedCoverageDialogProps {
  open: boolean;
  patientId: string;
  documentReferenceId: string;
  extraction: InsuranceCardExtraction;
  onClose: () => void;
  onSaved?: () => void;
}

interface FormState {
  payer_name: string;
  member_id: string;
  group_number: string;
  plan_name: string;
  relationship_to_subscriber: string;
  effective_date: string;
  subscriber_first_name: string;
  subscriber_last_name: string;
  subscriber_dob: string;
  subscriber_sex: string;
}

function emptyForm(): FormState {
  return {
    payer_name: '',
    member_id: '',
    group_number: '',
    plan_name: '',
    relationship_to_subscriber: 'self',
    effective_date: '',
    subscriber_first_name: '',
    subscriber_last_name: '',
    subscriber_dob: '',
    subscriber_sex: '',
  };
}

function nullIfBlank(value: string): string | null {
  return value.trim() === '' ? null : value;
}

function seedFromExtraction(e: InsuranceCardExtraction): FormState {
  return {
    payer_name: e.payer_name ?? '',
    member_id: e.member_id ?? '',
    group_number: e.group_number ?? '',
    plan_name: e.plan_name ?? '',
    relationship_to_subscriber: e.subscriber_relationship ?? 'self',
    effective_date: e.effective_date ?? '',
    subscriber_first_name: e.subscriber_first_name ?? '',
    subscriber_last_name: e.subscriber_last_name ?? '',
    // The insurance-card extractor doesn't emit subscriber DOB/sex —
    // the provider fills these in before saving a non-self coverage.
    subscriber_dob: '',
    subscriber_sex: '',
  };
}

export function SaveExtractedCoverageDialog({
  open,
  patientId,
  documentReferenceId,
  extraction,
  onClose,
  onSaved,
}: SaveExtractedCoverageDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [createCoverage, { isLoading }] = useCreatePatientCoverageMutation();
  const [form, setForm] = useState<FormState>(() => seedFromExtraction(extraction));
  const [showSubscriberErrors, setShowSubscriberErrors] = useState(false);

  // Re-seed when the dialog opens (extraction may have changed if the
  // user dismissed and re-opened with a fresh extraction).
  useEffect(() => {
    if (open) {
      setForm(seedFromExtraction(extraction));
      setShowSubscriberErrors(false);
    }
  }, [open, extraction]);

  const showSubscriber = form.relationship_to_subscriber !== 'self';
  const missingSubscriberFields =
    showSubscriber && (!form.subscriber_first_name.trim() || !form.subscriber_last_name.trim());

  const setField = (key: keyof FormState) => (value: string) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const handleSubmit = async (coverageOrder: 1 | 2) => {
    if (!form.payer_name.trim() && !form.member_id.trim()) {
      enqueueSnackbar('Add at least a payer name or member ID before saving.', {
        variant: 'warning',
      });
      return;
    }
    if (missingSubscriberFields) {
      setShowSubscriberErrors(true);
      enqueueSnackbar(
        'Subscriber first name and last name are required when the relationship is not self.',
        { variant: 'warning' },
      );
      return;
    }
    try {
      await createCoverage({
        patientId,
        createCoverageRequest: {
          // Blank optional fields must post as null, not "" — normalize
          // through `nullIfBlank`.
          payer_name: nullIfBlank(form.payer_name),
          member_id: nullIfBlank(form.member_id),
          group_number: nullIfBlank(form.group_number),
          plan_name: nullIfBlank(form.plan_name),
          relationship_to_subscriber: form.relationship_to_subscriber,
          effective_date: nullIfBlank(form.effective_date),
          subscriber_first_name: nullIfBlank(form.subscriber_first_name),
          subscriber_last_name: nullIfBlank(form.subscriber_last_name),
          subscriber_dob: nullIfBlank(form.subscriber_dob),
          subscriber_sex: nullIfBlank(form.subscriber_sex),
          coverage_order: coverageOrder,
          kind: 'insurance',
          // GDN-1032 follow-up: pairing these two fields causes the BE
          // to stamp acted_on_at on the source DocRef extraction. The
          // BE upserts when the slot is occupied (no 409) AS LONG AS
          // source_extraction_kind is set — both order=1 and order=2
          // can land safely.
          source_document_reference_id: documentReferenceId,
          source_extraction_kind: 'insurance_card',
        },
      }).unwrap();
      enqueueSnackbar(coverageOrder === 1 ? 'Primary coverage saved' : 'Secondary coverage saved', {
        variant: 'success',
      });
      onSaved?.();
    } catch (err) {
      console.error('Coverage save failed:', err);
      enqueueSnackbar('Failed to save coverage. Try again.', { variant: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth={'sm'}>
      <DialogTitle>Save coverage from insurance card</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Payer name"
            size="small"
            fullWidth
            value={form.payer_name}
            onChange={e => setField('payer_name')(e.target.value)}
            autoFocus
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              label="Member ID"
              size="small"
              fullWidth
              value={form.member_id}
              onChange={e => setField('member_id')(e.target.value)}
            />
            <TextField
              label="Group number"
              size="small"
              fullWidth
              value={form.group_number}
              onChange={e => setField('group_number')(e.target.value)}
            />
          </Box>
          <TextField
            label="Plan name"
            size="small"
            fullWidth
            value={form.plan_name}
            onChange={e => setField('plan_name')(e.target.value)}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              select
              label="Relationship to subscriber"
              size="small"
              fullWidth
              value={form.relationship_to_subscriber}
              onChange={e => setField('relationship_to_subscriber')(e.target.value)}
            >
              {RELATIONSHIP_OPTIONS.map(opt => (
                <MenuItem key={opt} value={opt} sx={{ textTransform: 'capitalize' }}>
                  {opt.charAt(0).toUpperCase() + opt.slice(1)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Effective date"
              size="small"
              fullWidth
              type="date"
              value={form.effective_date}
              onChange={e => setField('effective_date')(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Box>
          {showSubscriber && (
            <>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  label="Subscriber first name"
                  size="small"
                  fullWidth
                  required
                  value={form.subscriber_first_name}
                  onChange={e => setField('subscriber_first_name')(e.target.value)}
                  error={showSubscriberErrors && !form.subscriber_first_name.trim()}
                  helperText={
                    showSubscriberErrors && !form.subscriber_first_name.trim()
                      ? 'Required when relationship is not self'
                      : undefined
                  }
                />
                <TextField
                  label="Subscriber last name"
                  size="small"
                  fullWidth
                  required
                  value={form.subscriber_last_name}
                  onChange={e => setField('subscriber_last_name')(e.target.value)}
                  error={showSubscriberErrors && !form.subscriber_last_name.trim()}
                  helperText={
                    showSubscriberErrors && !form.subscriber_last_name.trim()
                      ? 'Required when relationship is not self'
                      : undefined
                  }
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  label="Subscriber date of birth"
                  size="small"
                  fullWidth
                  type="date"
                  value={form.subscriber_dob}
                  onChange={e => setField('subscriber_dob')(e.target.value)}
                  helperText={
                    !form.subscriber_dob.trim()
                      ? 'Required before claim submission; not required to save coverage or check eligibility.'
                      : undefined
                  }
                  slotProps={{ inputLabel: { shrink: true } }}
                />
                <TextField
                  select
                  label="Subscriber sex"
                  size="small"
                  fullWidth
                  value={form.subscriber_sex}
                  onChange={e => setField('subscriber_sex')(e.target.value)}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                </TextField>
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          onClick={() => void handleSubmit(2)}
          disabled={isLoading}
          sx={{ textTransform: 'none' }}
        >
          Save as secondary
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSubmit(1)}
          disabled={isLoading}
          sx={{ textTransform: 'none' }}
        >
          {isLoading ? 'Saving…' : 'Save as primary'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
